import React, { useEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import DateTimePicker from "react-datetime-picker";
import "react-datetime-picker/dist/DateTimePicker.css";
import { useNavigate } from 'react-router-dom';

import Breadcrumb from "../../common/components/Breadcrumb";
import CenteredLoader from "../../common/components/CenteredLoader";
import AuthProvider from "../../common/services/auth/AuthProvider/Index";
import {
  camelToDash,
  createAbortController,
  mergeErrors,
} from "../../common/utils";
import "./styles/main.css";
import { FormErrorsSchema } from "../../common/types";
import { Input } from "../../common/components/Form/Input";
import { Button } from "../../common/components/Form/Button";
import { Checkbox } from "../../common/components/Form/Checkbox";
import notifications from "../../common/services/notifications";

type AnalysisKitDataSchema = {
  tracking_number: string | null;
  lot_number: string;
  serial_number: string;
  status: string;
};

interface ProgressBarProps {
  step: number;
}

interface InitialStepProps {
  fetchData: Function;
  step: number;
}

interface StepProps {
  fetchData: Function;
  step: number;
  data: AnalysisKitDataSchema;
}

const changeStatus = async (serialNumber: string, targetStatus: string) => {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL
      }/v1/private/semen-analysis-kits/${serialNumber}/status/${camelToDash(
        targetStatus
      )}`;

    const response = await fetch(apiUrl, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
      },
      method: "PATCH",
    });

    if (!response.ok) {
      if (response.status === 422) {
        const result = await response.json();

        throw new Error(
          `Validation error: ${JSON.stringify(mergeErrors(result.errors))}.`
        );
      } else {
        throw new Error("Failed to load resource.");
      }
    }

    if (response.ok) {
      const result = await response.json();

      return result.data;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }

  return null;
};

const fetchInProgressData = async (abortSignal: AbortSignal | null = null) => {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/in-progress`;
    const response = await fetch(apiUrl, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
      },
      method: "GET",
      signal: abortSignal,
    });

    if (!response.ok) {
      if (response.status === 422) {
        const result = await response.json();

        throw new Error(
          `Validation error: ${JSON.stringify(mergeErrors(result.errors))}.`
        );
      } else {
        throw new Error("Failed to load resource.");
      }
    }

    if (response.ok) {
      const result = await response.json();

      return result.data;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

const fetchResults = async (serialNumber: string) => {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/results`;

    const response = await fetch(apiUrl, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
      },
      method: "GET",
    });

    if (!response.ok) {
      if (response.status === 422) {
        const result = await response.json();

        throw new Error(
          `Validation error: ${JSON.stringify(mergeErrors(result.errors))}.`
        );
      } else {
        throw new Error("Failed to load resource.");
      }
    }

    if (response.ok) {
      const result = await response.json();
      return result.data;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }

  return null;
};

const ProgressBar: React.FC<ProgressBarProps> = ({ step }) => {
  return (
    <>
      <h1 className="text-xl font-semibold text-gray-500 mb-6">
        {step === 1 &&
          "Step 1 of 4: Claim Your Kit."}
        {step === 2 && "Step 2 of 4: Confirm Your Shipping Label is Correct."}
        {step === 3 && "Step 3 of 4: Collect Your Sample."}
        {step === 4 && "Step 4 of 4: Mail Your Sample."}
        {/* {step === 5 &&
          "Step 5 of 4: The sample is on its way to the laboratory."}
        {step === 6 &&
          "Step 6 of 4: The sample is currently at the laboratory."}
        {step === 7 &&
          "Step 7 of 4: The sample analysis is complete, and you can now access the results."} */}
      </h1>

      <ol className="hidden md:flex items-center w-full">
        <li
          className={`flex w-full items-center after:content-[''] after:w-full after:h-1 after:border-b ${step > 1 ? "after:border-brand-blue-400" : "after:border-gray-300"
            } after:border-4 after:inline-block`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 ${step >= 1
              ? "bg-brand-blue-400 text-white"
              : "bg-gray-300 text-gray-400"
              } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            1
          </span>
        </li>
        <li
          className={`flex w-full items-center after:content-[''] after:w-full after:h-1 after:border-b ${step > 2 ? "after:border-brand-blue-400" : "after:border-gray-300"
            } after:border-4 after:inline-block`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 ${step >= 2
              ? "bg-brand-blue-400 text-white"
              : "bg-gray-300 text-gray-400"
              } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            2
          </span>
        </li>
        <li
          className={`flex w-full items-center after:content-[''] after:w-full after:h-1 after:border-b ${step > 3 ? "after:border-brand-blue-400" : "after:border-gray-300"
            } after:border-4 after:inline-block`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 ${step >= 3
              ? "bg-brand-blue-400 text-white"
              : "bg-gray-300 text-gray-400"
              } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            3
          </span>
        </li>
        <li
          className={`flex items-center`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 ${step >= 4
              ? "bg-brand-blue-400 text-white"
              : "bg-gray-300 text-gray-400"
              } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            4
          </span>
        </li>
        {/* <li
          className={`flex w-full items-center after:content-[''] after:w-full after:h-1 after:border-b ${
            step > 5 ? "after:border-brand-blue-400" : "after:border-gray-300"
          } after:border-4 after:inline-block`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 ${
              step >= 5
                ? "bg-brand-blue-400 text-white"
                : "bg-gray-300 text-gray-400"
            } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            5
          </span>
        </li>
        <li
          className={`flex w-full items-center after:content-[''] after:w-full after:h-1 after:border-b ${
            step > 6 ? "after:border-brand-blue-400" : "after:border-gray-300"
          } after:border-4 after:inline-block`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 ${
              step >= 6
                ? "bg-brand-blue-400 text-white"
                : "bg-gray-300 text-gray-400"
            } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            6
          </span>
        </li>
        <li className={`flex items-center w-full`}>
          <span
            className={`flex items-center justify-center w-10 h-10 ${
              step >= 7
                ? "bg-brand-blue-400 text-white"
                : "bg-gray-300 text-gray-400"
            } rounded-full lg:h-12 lg:w-12 shrink-0`}
          >
            7
          </span>
        </li> */}
      </ol>
    </>
  );
};

const InitialStep1: React.FC<InitialStepProps> = ({ step, fetchData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [ageConfirmation, setAgeConfirmation] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const [serialNumber, setSerialNumber] = useState<string>("");

  const [vasectomyDate, setVasectomyDate] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleVasectomyDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVasectomyDate(event.target.value);
  };

  const handleOnClickNext = async () => {
    // Check if the vasectomy date is at least 12 weeks prior to the current date
    const vasectomyDateObj = new Date(vasectomyDate);
    const twelveWeeksAgo = new Date();
    twelveWeeksAgo.setDate(twelveWeeksAgo.getDate() - 84); // 12 weeks * 7 days

    if (vasectomyDateObj > twelveWeeksAgo) {
      setShowConfirmation(true);
      return;
    }

    try {
      setLoading(true);
      setFormErrors({});

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/status/claimed`;

      const response = await fetch(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        body: JSON.stringify({
          procedure_date: vasectomyDate,
        }),
        method: "PATCH",
      });

      if (!response.ok) {
        if (response.status === 422) {
          setFormErrors({
            serialNumber: [
              "The given serial number does not have the right format, please try again or contact support at support@jackathome.com.",
            ],
          });
        } else if (response.status === 404) {
          setFormErrors({
            serialNumber: [
              "The given serial number does not exists, please try again or contact support at support@jackathome.com.",
            ],
          });
        } else {
          throw new Error("Failed to load resource");
        }
      } else {
        notifications.success({ message: "The semen analysis kit has been successfully claimed." });
        fetchData();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      notifications.error({ message: "Something went wrong while trying to claim the semen analysis kit." });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationResponse = async (confirm: boolean) => {
    setShowConfirmation(false);
    if (confirm) {
      try {
        setLoading(true);
        setFormErrors({});

        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/status/claimed`;

        const response = await fetch(apiUrl, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
          },
          body: JSON.stringify({
            procedure_date: vasectomyDate,
          }),
          method: "PATCH",
        });

        if (!response.ok) {
          if (response.status === 422) {
            setFormErrors({
              serialNumber: [
                "The given serial number does not have the right format, please try again or contact support at support@jackathome.com.",
              ],
            });
          } else if (response.status === 404) {
            setFormErrors({
              serialNumber: [
                "The given serial number does not exists, please try again or contact support at support@jackathome.com.",
              ],
            });
          } else {
            throw new Error("Failed to load resource");
          }
        } else {
          notifications.success({ message: "The semen analysis kit has been successfully claimed." });
          fetchData();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        notifications.error({ message: "Something went wrong while trying to claim the semen analysis kit." });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full mt-6 pt-4">
        <div className="w-full mt-6">
          <div className="md:grid md:grid-cols-2 gap-4 bg-white">
            <div className="mb-4 md:mb-0">
              <Input
                label="Please type the number located on the barcode of your transport tube"
                name="serialNumber"
                placeholder="XXXX-XXXX-XXXX"
                errors={formErrors}
                value={serialNumber}
                setValue={setSerialNumber}
                disabled={loading}
                inputMask={(inputText: string) => {
                  const cleanText = inputText
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "");

                  let formattedText = "";
                  for (let i = 0; i < cleanText.length; i++) {
                    if (i > 0 && i % 4 === 0) {
                      formattedText += "-";
                    }
                    formattedText += cleanText[i];
                  }

                  return formattedText.slice(0, 14);
                }}
              />
              <div className="w-full mt-6">
                <label htmlFor="vasectomyDate">When was your vasectomy procedure performed?</label>
                <input
                  type="date"
                  id="vasectomyDate"
                  name="vasectomyDate"
                  value={vasectomyDate}
                  onChange={handleVasectomyDateChange}
                  disabled={loading}
                  style={{minWidth: 137}}
                />
              </div>
              <div className="w-full mt-6">
                <Checkbox
                  label="I confirm I am 18 years of age or older"
                  name="confirm"
                  checked={ageConfirmation}
                  onChange={async () => { setAgeConfirmation(!ageConfirmation) }}
                />
              </div>
            </div>
            <div className="mb-4 md:mb-0">
              <img
                className="mt-6 max-h-80 mx-auto"
                src="../images/transport.png"
                alt="Transport Tube"
              />
            </div>
          </div>
        </div>
        {showConfirmation && (
          <div className="confirmation-dialog">
            <p>It is recommended that you wait at least 12 weeks after your procedure before submitting a sample for testing. Would you still like to continue?</p>
            <Button
              label="Yes"
              theme="primary"
              size="medium"
              className="block w-full md:ml-auto md:w-auto mb-2"
              onClick={() => handleConfirmationResponse(true)}
            />
            <Button
              label="No"
              theme="secondary"
              size="medium"
              className="block w-full md:ml-auto md:w-auto"
              onClick={() => handleConfirmationResponse(false)}
            />
          </div>
        )}
        {!showConfirmation && (
          <div className="w-full mt-12 lg:mt-24">
            <Button
              label="Claim My Kit"
              theme="primary"
              size="medium"
              className="block w-full md:ml-auto md:w-auto"
              onClick={handleOnClickNext}
              disabled={serialNumber.length < 14 || loading || vasectomyDate === "" || !ageConfirmation}
            />
          </div>
        )}
      </div>
    </>
  );
};

const ClaimedStep2: React.FC<StepProps> = ({ step, fetchData, data }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const [trackingNumber, setTrackingNumber] = useState<string>(
    data.tracking_number || ""
  );
  const [editMode, setEditMode] = useState<boolean>(false);

  const handleToggleEdit = () => {
    setEditMode(!editMode);
  };

  const handleOnClickNext = async () => {
    setLoading(true);

    const saveTrackingNumber = async () => {
      try {
        setFormErrors({});

        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${data.serial_number}/tracking-number`;

        const response = await fetch(apiUrl, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
          },
          method: "PATCH",
          body: JSON.stringify({
            tracking_number: trackingNumber,
          }),
        });

        if (!response.ok) {
          if (response.status === 422) {
            const errorData = await response.json();
            setFormErrors(mergeErrors(errorData.errors));
          } else {
            throw new Error("Failed to load resource");
          }
        }

        if (response.ok) {
          notifications.success({ message: "The tracking number has been successfully updated." });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        notifications.error({ message: "Something went wrong while trying to update the tracking number." });
      }
    };

    if (editMode) {
      await saveTrackingNumber();
    }

    await changeStatus(data.serial_number, "sampleCollectionInProgress");
    fetchData();
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full mt-6 pt-4">
        <div className="w-full lg:w-1/2 mt-6">
          {!editMode ? (
            <div className="mt-2 text-gray-600">
              Locate the tracking number on the provided shipping label as seen to the right. Confirm that the tracking label number below matches the number located below. If it does not match - please input the correct number
              <Button
                label="here."
                theme="text-primary"
                className="font-bold px-2"
                onClick={handleToggleEdit}
                disabled={loading}
              />
              <br />
              <br />
              The current tracking number associated with your kit is: <b>{data.tracking_number}</b>
            </div>
          ) : (
            <>
              <Input
                label="Please type the right tracking number"
                name="tracking_number"
                placeholder="XXXX-XXXX-XXXX"
                errors={formErrors}
                value={trackingNumber}
                setValue={setTrackingNumber}
                disabled={!editMode || loading}
              />
              <Button
                label="Cancel edit"
                theme="text-primary"
                className="font-bold py-2"
                onClick={handleToggleEdit}
                disabled={loading}
              />
              <br />
              <br />
              <p>Please ensure it is accurate; otherwise, you won't receive updates until the package arrives at the laboratory.</p>
            </>
          )}
        </div>
        <img
          className="mt-6"
          src="../images/UPS-Shipping-Label.jpg"
          alt="Shipping Label"
        />
        <div className="w-full mt-12 lg:mt-24">
          <Button
            label={editMode ? "Update & next" : "Next"}
            theme="primary"
            size="medium"
            className="block w-full md:ml-auto md:w-auto"
            onClick={handleOnClickNext}
            disabled={(editMode && trackingNumber.length === 0) || loading}
          />
        </div>
      </div>
    </>
  );
};

const SampleCollectionInProgressStep3: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sampleCollectionDate, setSampleCollectionDate] = useState<Date | null>(
    new Date()
  );

  const saveSampleCollectionTimesamp = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${data.serial_number}/sample-collection-timestamp`;

      const response = await fetch(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        method: "PATCH",
        body: JSON.stringify({
          sample_collection_timestamp: sampleCollectionDate?.toISOString(),
        }),
      });

      if (!response.ok) {
        if (response.status === 422) {
          const result = await response.json();

          throw new Error(
            `Validation error: ${JSON.stringify(mergeErrors(result.errors))}.`
          );
        } else {
          throw new Error("Failed to load resource.");
        }
      }

      if (response.ok) {
        return true;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    return false;
  };

  const handleOnClickNext = async () => {
    setLoading(true);
    if (await saveSampleCollectionTimesamp()) {
      await changeStatus(data.serial_number, "preparingForShippingToLab");
      fetchData();
    }
  };

  return (
    <>
      <ProgressBar step={step} />
      <div className="w-full mt-12">
        <div className="w-full">
          <div className="md:grid md:grid-cols-2 gap-4 mb-5">
            <div>
              <p className="mb-5">
                Please follow the <a href="https://jackathome.com/pages/instructions" target="_blank" rel="noreferrer">detailed instructions</a> that came with your kit to collect your semen sample.
              </p>
              <p>
                <b>Once you’ve followed the instructions and your sample has been correctly collected</b>, input the date of the collection below.
              </p>
            </div>
            <div>
              <img
                className="mt-6 w-120 "
                src="../images/fist.png"
                alt="Fist"
              />
            </div>
          </div>


          <DateTimePicker
            amPmAriaLabel="Select AM/PM"
            calendarAriaLabel="Toggle calendar"
            clearAriaLabel="Clear value"
            dayAriaLabel="Day"
            hourAriaLabel="Hour"
            maxDetail="second"
            minuteAriaLabel="Minute"
            monthAriaLabel="Month"
            nativeInputAriaLabel="Date and time"
            onChange={setSampleCollectionDate}
            secondAriaLabel="Second"
            value={sampleCollectionDate}
            yearAriaLabel="Year"
          />
        </div>
      </div>
      <div className="w-full mt-12">
        <Button
          label="Done, Next"
          theme="primary"
          size="medium"
          className="block w-full md:ml-auto md:w-auto"
          onClick={handleOnClickNext}
          disabled={loading || !sampleCollectionDate}
        />
      </div>
    </>
  );
};

const PreparingForShippingToLabStep4: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  const navigate = useNavigate();
  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full">
        <div className="md:grid md:grid-cols-2 gap-4 mb-5 mt-8">
          <div>
            <p>
              Securely seal the kit box and affix the shipping label over top of the Jack logo.
            </p>

            <p className="mt-6">
              Please drop your kit off at your nearest UPS store <b>today</b> and let us know you’ve shipped your kit by clicking the button below.
            </p>
          </div>
          <div>
            <img
              className="mt-6 w-120 mx-auto"
              src="../images/return-box.png"
              alt="Return Box"
            />
          </div>
        </div>
      </div>
      <div className="w-full mt-12">
        <Button
          label="I've Mailed My Kit"
          theme="primary"
          size="medium"
          className="block w-full md:ml-auto md:w-auto"
          onClick={async () => {
            notifications.success({ message: "Great! We'll keep an eye out for your kit. You’ll be notified via email when your test results are ready." });
            navigate('/history')
          }}
        />
      </div>
    </>
  );
};

const ShippingToLabInProgressStep5: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full lg:w-1/2 mt-12">
        <p>
          The sample is on the way to the laboratory, we will keep you updated.
        </p>
        <img
          className="mt-6 w-120 h-120"
          src="../images/sample_shipping.png"
          alt="Sample"
        />
      </div>
    </>
  );
};

const ShippingToLabCompletedStep6: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full lg:w-1/2 mt-12">
        <p>
          The sample has arrived to the laboratory, we will keep you updated.
        </p>
        <img
          className="mt-6 w-120 h-120"
          src="../images/sample_in_lab.png"
          alt="Sample"
        />
      </div>
    </>
  );
};

const SuccessfullyProcessedByLabStep7: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);

  const handleCheckboxChange = async () => {
    setChecked(!checked);
  };

  const handleOnClickNext = async () => {
    setDisableNextButton(true);
    setLoading(true);
    await changeStatus(data.serial_number, "completed");
    notifications.success({ message: "The active analysis has been successfully completed. We're taking you to your results!" });
    const resultsFromApi = await fetchResults(data.serial_number);
    navigate('/results', { state: { data: resultsFromApi } });
    fetchData();
    setLoading(false);
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full mt-6">
        <p className="text-base leading-relaxed font-bold text-gray-950 mb-6 mt-6">
          Almost done! Just a quick thumbs-up to the terms and conditions below,
          and we can show you the results:
        </p>
        <Checkbox
          label="I understand and accept that by exporting or printing my medical
          exam results, I take full responsibility for the security of my
          personal medical data, as once it is outside the system, CARE FOR
          THERE LLC cannot guarantee its protection to comply with HIPAA
          (Health Insurance Portability and Accountability Act) privacy
          regulations. I agree that any unauthorized disclosure of this
          information is at my own risk."
          name="agree"
          checked={checked}
          disabled={loading}
          onChange={handleCheckboxChange}
        />

        {loading && <CenteredLoader className="flex mt-12 mb-6" />}

        {!loading && checked && (
          <CenteredLoader className="flex mt-12 mb-6" />
        )}
      </div>
      <div className="w-full mt-24">
        <Button
          label="Finalize"
          theme="primary"
          size="medium"
          className="block w-full md:ml-auto md:w-auto"
          onClick={handleOnClickNext}
          disabled={!checked || loading || disableNextButton}
        />
      </div>
    </>
  );
};

const Active = () => {
  const [fetchDataTrigger, setFetchDataTrigger] = useState<number>(0);
  const [step, setStep] = useState<string>("initial");
  const [data, setData] = useState<AnalysisKitDataSchema | null>(null);
  const [loading, setLoading] = useState(true);

  /*useEffect(() => {
    setFetchDataTrigger(Date.now())
  }, [])*/

  useEffect(() => {
    const { abortController, abortSignal } = createAbortController();

    setLoading(true);

    fetchInProgressData(abortSignal)
      .then((data) => {
        if (data) {
          setData(data);
          setStep(data.status);
        } else {
          setData(null);
          setStep("initial");
          notifications.info({ message: "No active analysis found, you are now about to start a new analysis." });
        }

        setLoading(false);
      })
      .catch(console.error);

    return () => {
      abortController.abort();
    };
  }, [fetchDataTrigger]);

  const fetchDataTriggerHandle = () => {
    setFetchDataTrigger(Date.now());
  };

  return (
    <>
      <div className="w-full lg:w-3/4 min-h-screen bg-gray-100 text-gray-900">
        <main className="mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Breadcrumb
            pages={[
              { name: "Kit Registration", href: "#", current: true },
            ]}
          />

          <div className="mt-8">
            <h1 className="text-xl font-semibold">Kit Registration</h1>
          </div>

          <div className="bg-white mt-6 mb-6 p-6">
            {loading && <CenteredLoader className="flex w-full mt-48 mb-48" />}
            {!loading && step === "initial" && (
              <InitialStep1 step={1} fetchData={fetchDataTriggerHandle} />
            )}
            {!loading && data && step === "claimed" && (
              <ClaimedStep2
                step={2}
                fetchData={fetchDataTriggerHandle}
                data={data}
              />
            )}
            {!loading && data && step === "sampleCollectionInProgress" && (
              <SampleCollectionInProgressStep3
                step={3}
                fetchData={fetchDataTriggerHandle}
                data={data}
              />
            )}
            {!loading && data && step === "preparingForShippingToLab" && (
              <PreparingForShippingToLabStep4
                step={4}
                fetchData={fetchDataTriggerHandle}
                data={data}
              />
            )}
            {!loading && data && step === "shippingToLabInProgress" && (
              <ShippingToLabInProgressStep5
                step={5}
                fetchData={fetchDataTriggerHandle}
                data={data}
              />
            )}
            {!loading && data && step === "shippingToLabCompleted" && (
              <ShippingToLabCompletedStep6
                step={6}
                fetchData={fetchDataTriggerHandle}
                data={data}
              />
            )}
            {!loading && data && step === "successfullyProcessedByLab" && (
              <SuccessfullyProcessedByLabStep7
                step={7}
                fetchData={fetchDataTriggerHandle}
                data={data}
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Active;
